"use client";

import { useEffect, useState } from "react";
import type { ResolvedCollection } from "../../hooks/useLifestyleCollections";
import type { PrizeCategory } from "../../lifestyle-constants";
import { CloseIcon } from "../lifestyleIcons";
import CategoryGrid from "./CategoryGrid";
import CollectionList from "./CollectionList";
import FilterTab from "./FilterTab";

export type FilterModalTab = "collections" | "categories";

interface FilterOptionsModalProps {
  isOpen: boolean;
  /** Which tab the filter bar button that opened the modal maps onto. */
  initialTab: FilterModalTab;
  collections: ResolvedCollection[];
  selectedCategory: PrizeCategory | null;
  /** Prize counts per category slug, used to grey out empty categories. */
  categoryCounts: Map<string, number>;
  /** Collections are jump links: this scrolls the page to that carousel row. */
  onScrollToCollection: (collectionId: number) => void;
  onSelectCategory: (category: PrizeCategory | null) => void;
  onClose: () => void;
}

/**
 * The Collections / Categories picker. Both filter bar buttons open this same
 * modal and only differ by which tab starts active, so switching between the
 * two lists costs no extra click.
 *
 * The two tabs do different jobs: a category narrows the results, while a
 * collection just scrolls the page to its row. Either way the modal closes on
 * pick.
 */
export default function FilterOptionsModal({
  isOpen,
  initialTab,
  collections,
  selectedCategory,
  categoryCounts,
  onScrollToCollection,
  onSelectCategory,
  onClose,
}: FilterOptionsModalProps) {
  const [activeTab, setActiveTab] = useState<FilterModalTab>(initialTab);

  // Each open re-syncs to whichever button was pressed, but tab changes while
  // open are the user's to keep.
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);

    // Stop the page behind the modal from scrolling.
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = overflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const select = (apply: () => void) => {
    apply();
    onClose();
  };

  return (
    // Matches the z-level of FiltersDrawer so the site header at z-[1003] and
    // its popovers stay behind the modal.
    <div className="fixed inset-0 z-[9999] flex items-center justify-center lg:p-3 font-jost">
      <button
        type="button"
        onClick={onClose}
        aria-label="Close filters"
        className="absolute inset-0 bg-black/50 cursor-default"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Filter prizes"
        className="relative flex flex-col w-full lg:w-[800px] h-full lg:h-auto lg:max-h-[calc(100vh-96px)] bg-white lg:rounded-2xl lg:shadow-xl overflow-hidden">
        <div className="flex flex-col items-end w-full">
          <div className="flex items-center pr-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              aria-label="Close filters"
              className="flex items-center justify-center size-6 text-gray-5 hover:text-dark-1 transition-colors">
              <CloseIcon className="size-6" />
            </button>
          </div>

          <div role="tablist" aria-label="Filter type" className="flex items-center w-full border-b border-black/8">
            <FilterTab
              label="Collections"
              isActive={activeTab === "collections"}
              controls="lifestyle-filter-collections"
              onSelect={() => setActiveTab("collections")}
            />
            <FilterTab
              label="Categories"
              isActive={activeTab === "categories"}
              controls="lifestyle-filter-categories"
              onSelect={() => setActiveTab("categories")}
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 lg:px-8 pt-3 lg:pt-6 pb-8 lg:pb-6">
          {activeTab === "collections" ? (
            /*
             * Invisible category grid sets the panel height so switching tabs
             * does not resize the modal; collections scroll inside that box.
             */
            <div className="relative">
              <CategoryGrid
                selectedCategory={selectedCategory}
                categoryCounts={categoryCounts}
                onSelect={() => {}}
                className="invisible pointer-events-none select-none"
              />
              <div className="absolute inset-0 overflow-y-auto">
                <CollectionList
                  id="lifestyle-filter-collections"
                  collections={collections}
                  onSelect={id => select(() => onScrollToCollection(id))}
                />
              </div>
            </div>
          ) : (
            <CategoryGrid
              id="lifestyle-filter-categories"
              selectedCategory={selectedCategory}
              categoryCounts={categoryCounts}
              onSelect={category => select(() => onSelectCategory(category))}
            />
          )}
        </div>
      </div>
    </div>
  );
}
